import React, { useEffect, useState } from "react";
import axios from "../axiosSetup";
import { FiX } from "react-icons/fi";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import EditRoleAccessModal from "./EditRoleAccessModal";
import "./ViewUserRoleModal.css";
import { getUser } from "../auth";

/* ================= CONSTANTS ================= */

const PAGES = [
  "User Management",
  "Product Management",
  "Customer Pricing Management",
  "Margin Calculator",
  "Customer Order",
  "PO Templates",
];

const SERVICE_TO_PAGE = {
  "User Management": "User Management",
  "Product Management": "Product Management",
  "Customer Price Management": "Customer Pricing Management",
  "Margin Calculator Management": "Margin Calculator",
  "Customer Orders Management": "Customer Order",
  "Customer Quotes Management": "PO Templates",
};

const ROLES = [
  "Admin",
  "Product Owner",
  "Product User",
  "Vocovo Viewer",
];

const HEADERS = ["Pages", ...ROLES];

/* ===== PERMISSION SHORT MAP ===== */

const PERM_SHORT_MAP = {
  Create: "C",
  Read: "R",
  Update: "U",
  Delete: "D",
  Approve: "A",
};

const toShortPerms = (permString = "") => {
  if (!permString) return "No Access";

  return permString
    .split(",")
    .map((p) => PERM_SHORT_MAP[p.trim()])
    .filter(Boolean)
    .join(", ");
};


/* ================= COMPONENT ================= */

const ViewUserRoleModal = ({ open, onClose }) => {
  const user = getUser();
  const isAdmin =
    String(user?.role || "").trim().toLowerCase() === "admin";

  const [editOpen, setEditOpen] = useState(false);
  const [roleTable, setRoleTable] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ===== FETCH AUTHORIZATION TABLE ===== */

  const fetchAuthorizationTable = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/authorization");
      const data = Array.isArray(res.data) ? res.data : [];

      const table = PAGES.map((p) => ({ page: p }));

      data.forEach(({ role, services }) => {
        services?.forEach(({ service, operations }) => {
          const page = SERVICE_TO_PAGE[service];
          if (!page) return;

          const row = table.find((r) => r.page === page);
          if (!row) return;

          row[role] = operations
            .map(
              (op) => op.charAt(0) + op.slice(1).toLowerCase()
            )
            .join(", ");
        });
      });

      setRoleTable(table);
    } finally {
      setLoading(false);
    }
  };

  /* ===== LOAD ON OPEN ===== */

  useEffect(() => {
    if (open) {
      fetchAuthorizationTable();
    }
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="modal role-view-modal">
          {/* HEADER */}
          <div className="modal-header">
            <div className="title-inline">
              <h2 className="title-text">User Role Permissions</h2>
                <span className="urp-legend">
              (C – Create, R – Read, U – Update, D - Delete, A - Approve)
            </span>
              {isAdmin && (
                <span
                  className="edit-link"
                  onClick={() => setEditOpen(true)}
                >
                  Edit user role access
                </span>
              )}
            </div>

            <FiX
              className="modal-close-icon"
              onClick={onClose}
            />
          </div>

          {/* TABLE */}
          <div className="modal-body">
            {loading ? (
              <div style={{ minHeight: "180px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 30, color: "#f50087" }} spin />} />
                <span style={{ color: "#f50087", fontWeight: 600 }}>Loading</span>
              </div>
            ) : (
            <table className="role-permission-table">
              <thead>
                <tr>
                  {HEADERS.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {roleTable.map((row) => (
                  <tr key={row.page}>
                    <td>{row.page}</td>

                    {ROLES.map((r) => (
                      <td key={r}>
                        {toShortPerms(row[r])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      <EditRoleAccessModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onUpdated={() => {
          setEditOpen(false);
          fetchAuthorizationTable(); //  refresh after update
        }}
      />
    </>
  );
};

export default ViewUserRoleModal;
